/**
 * ScheduleEventModal — Tier-3 redesign of the "schedule this content" flow.
 *
 * Replaces the old form-style quick-schedule modal with a structured
 * "calendar event for content" experience built around three sections:
 *
 *   WHERE   — multi-screen picker with per-screen aspect-ratio compatibility
 *   WHEN    — quick presets, recurrence, duration display, conflict detection
 *   PREVIEW — live sentence describing what will happen + conflict summary
 *
 * Phase A scope (this file):
 *   - Content header with thumbnail + dimensions/type
 *   - WHERE section: search, AR-aware screen list, ✓ matches feedback,
 *     incompatible disabled with explanation
 *   - WHEN section: date range, time range with duration, recurrence
 *     dropdown (Once / Every day / Weekdays / Weekends / Custom days),
 *     quick preset chips
 *   - PREVIEW section: live sentence + conflict detection from a single
 *     getCmsSchedules fetch
 *   - Proper button pluralization, helpful empty/loading states
 *
 * Phases B–E (timeline viz, drag-to-resize, multi-screen stack, templates)
 * extend this file in subsequent commits — see CLAUDE.md.
 *
 * Props:
 *   item       — content descriptor: { id, contentId, title, url, width, height, type }
 *                (matches the shape used by quick-schedule callers)
 *   tvs        — array of devices with { deviceId, tvId, displayName, location,
 *                                        nativeResolution: {width,height,aspectRatio},
 *                                        status }
 *   open       — controls visibility
 *   onClose    — () => void
 *   onScheduled — (results) => void  fired after a successful schedule
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { X, Calendar, Clock, Monitor, Check, AlertTriangle, Bookmark, Trash2 } from 'lucide-react';
import { getCmsSchedules } from '../api/contentApi';
import { createScheduleFromLegacyShape } from '../api/schedulesApi';
import { getActiveProjectId } from '../constants/projectConstants';
import { checkAspectRatioMatch } from '../utils/mediaValidation';
import { getDeviceDisplayName, getResolutionLabel } from '../utils/deviceLabel';
import { getScreenCategoryFromDeviceId, formatCategoryLabel, categorySortKey } from '../utils/screenCategories';
import ScheduleTimeline from './ScheduleTimeline';
import { listTemplatesSorted, saveTemplate, deleteTemplate, datesForTemplate } from '../utils/scheduleTemplates';

// ─── helpers ───────────────────────────────────────────────────────────

const pad2 = (n) => String(n).padStart(2, '0');

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const addDaysISO = (isoDate, days) => {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/** "9:00 AM → 6:00 PM" → "9 hours" / "1 hour 30 min" / "45 min".
 *  Overnight-aware: if end < start we wrap to the next day so 20:13→09:00
 *  comes out as "12h 47m" rather than empty. */
const formatDuration = (startStr, endStr) => {
  if (!startStr || !endStr) return '';
  const [sh, sm] = startStr.split(':').map(Number);
  const [eh, em] = endStr.split(':').map(Number);
  if ([sh, sm, eh, em].some((v) => Number.isNaN(v))) return '';
  const sTotal = sh * 60 + sm;
  const eTotal = eh * 60 + em;
  let mins = eTotal - sTotal;
  if (mins < 0) mins += 24 * 60;
  if (mins === 0) return '';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (m === 0) return `${h} hour${h === 1 ? '' : 's'}`;
  return `${h}h ${m}m`;
};

/** Friendly "Apr 10" or "Apr 10 → Apr 17" */
const formatDateRange = (startISO, endISO) => {
  if (!startISO) return '';
  const opt = { month: 'short', day: 'numeric' };
  const a = new Date(startISO).toLocaleDateString('en-US', opt);
  if (!endISO || endISO === startISO) return a;
  const b = new Date(endISO).toLocaleDateString('en-US', opt);
  return `${a} → ${b}`;
};

/** Convert HH:MM 24h to "9:00 AM" / "11:30 PM" */
const formatTimeReadable = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${pad2(m)} ${period}`;
};

const timeToMinutes = (hhmm) => {
  if (!hhmm) return -1;
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return -1;
  return h * 60 + m;
};

const slotsOverlap = (aStart, aEnd, bStart, bEnd) =>
  aStart < bEnd && bStart < aEnd;

// ─── presets ───────────────────────────────────────────────────────────
//
// Recurrence is implicit in the date range: an intent plays every day between
// startDate and endDate at the given time window. Weekdays/weekends filtering
// was removed — not a mental model that fits Indian signage usage. A week is
// a week, no days off.

const buildPresets = () => {
  const today = todayISO();
  const week = addDaysISO(today, 6);
  const fortnight = addDaysISO(today, 13);
  const month = addDaysISO(today, 29);
  // Presets are all-day only. Narrow-time-window cases (café morning menu,
  // happy hour, "right now, 1 hour") route through Custom where the user
  // explicitly unchecks All-day — signage 99% needs 24h coverage.
  return [
    {
      id: 'today-all',
      label: 'Today, all day',
      apply: () => ({ startDate: today, endDate: today, startTime: '00:00', endTime: '23:59', allDay: true }),
    },
    {
      id: 'week-all',
      label: 'This week, all day',
      apply: () => ({ startDate: today, endDate: week, startTime: '00:00', endTime: '23:59', allDay: true }),
    },
    {
      id: 'fortnight-all',
      label: 'Next 2 weeks, all day',
      apply: () => ({ startDate: today, endDate: fortnight, startTime: '00:00', endTime: '23:59', allDay: true }),
    },
    {
      id: 'month-all',
      label: 'Next 30 days, all day',
      apply: () => ({ startDate: today, endDate: month, startTime: '00:00', endTime: '23:59', allDay: true }),
    },
  ];
};

// ─── compatibility ─────────────────────────────────────────────────────

/**
 * Classify screen vs content aspect ratio.
 * compatible / incompatible / unknown_content / unknown_screen
 *
 * Distinguish which side is missing because the product rule differs:
 *   - unknown_content → block all screens (can't verify → any play is a gamble).
 *   - unknown_screen  → allow (profile/heartbeat just hasn't populated yet).
 */
const classifyScreen = (screen, contentWidth, contentHeight) => {
  const dims = screen?.profileContent || screen?.nativeResolution;
  const sw = dims?.width || 0;
  const sh = dims?.height || 0;
  if (!contentWidth || !contentHeight) return { status: 'unknown_content' };
  if (!sw || !sh) return { status: 'unknown_screen' };
  const r = checkAspectRatioMatch(contentWidth, contentHeight, sw, sh);
  return { status: r.valid ? 'compatible' : 'incompatible', ...r };
};

// ─── styles ────────────────────────────────────────────────────────────

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.5)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '40px 24px',
    overflowY: 'auto',
  },
  modal: {
    background: '#fff',
    borderRadius: 16,
    width: 720,
    maxWidth: '100%',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'calc(100vh - 80px)',
  },
  header: {
    padding: '20px 24px 18px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  title: { margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' },
  closeBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#64748b',
    padding: 4,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
  },
  contentBlock: {
    padding: '16px 24px',
    borderBottom: '1px solid #f1f5f9',
    display: 'flex',
    gap: 14,
    alignItems: 'center',
    background: '#f8fafc',
  },
  thumb: {
    width: 64,
    height: 64,
    borderRadius: 8,
    objectFit: 'cover',
    border: '1px solid #e2e8f0',
    background: '#e2e8f0',
    flexShrink: 0,
  },
  contentName: { fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 2 },
  contentMeta: { fontSize: 12, color: '#64748b' },
  body: { padding: '16px 24px', overflowY: 'auto', flex: 1 },
  section: { marginBottom: 18 },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: 8,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  field: { marginBottom: 12 },
  label: { display: 'block', fontSize: 12, color: '#475569', marginBottom: 4, fontWeight: 500 },
  input: {
    width: '100%',
    padding: '8px 10px',
    fontSize: 14,
    border: '1px solid #d1d5db',
    borderRadius: 8,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  presetRow: { display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 },
  presetChip: (active) => ({
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 16,
    border: `1px solid ${active ? '#c8a147' : '#e2e8f0'}`,
    background: active ? 'rgba(200, 161, 71, 0.15)' : '#fff',
    color: active ? '#92400e' : '#475569',
    cursor: 'pointer',
    transition: 'all 0.15s',
  }),
  screenList: {
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    maxHeight: 200,
    overflowY: 'auto',
  },
  screenRow: (checked, blocked) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '8px 12px',
    cursor: blocked ? 'not-allowed' : 'pointer',
    fontSize: 13,
    borderBottom: '1px solid #f1f5f9',
    background: blocked ? '#fef2f2' : checked ? '#fef9e7' : '#fff',
    opacity: blocked ? 0.6 : 1,
  }),
  catHeader: {
    padding: '6px 12px',
    fontSize: 10,
    fontWeight: 700,
    color: '#64748b',
    textTransform: 'uppercase',
    background: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  preview: {
    background: 'linear-gradient(135deg, #fef9e7 0%, #fef3c7 100%)',
    border: '1px solid rgba(200, 161, 71, 0.35)',
    borderRadius: 10,
    padding: '14px 16px',
    fontSize: 13,
    color: '#78350f',
    lineHeight: 1.55,
  },
  conflictNone: { color: '#15803d', display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 12 },
  conflictHas: { color: '#b45309', display: 'flex', alignItems: 'flex-start', gap: 6, marginTop: 8, fontSize: 12 },
  footer: {
    padding: '14px 24px',
    borderTop: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#f8fafc',
  },
  cancelBtn: {
    padding: '9px 18px',
    fontSize: 14,
    fontWeight: 600,
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    background: '#fff',
    color: '#475569',
    cursor: 'pointer',
  },
  primaryBtn: (disabled) => ({
    padding: '9px 22px',
    fontSize: 14,
    fontWeight: 700,
    border: 'none',
    borderRadius: 8,
    background: disabled ? '#cbd5e1' : '#c8a147',
    color: '#fff',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background 0.15s',
  }),
  errorBox: {
    padding: '8px 12px',
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 6,
    color: '#b91c1c',
    fontSize: 12,
    marginTop: 8,
  },
};

// ─── component ─────────────────────────────────────────────────────────

const ScheduleEventModal = ({ item, tvs = [], open, onClose, onScheduled }) => {
  const [selectedScreenIds, setSelectedScreenIds] = useState([]);
  const [screenSearch, setScreenSearch] = useState('');
  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(addDaysISO(todayISO(), 6));
  const [startTime, setStartTime] = useState(() => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });
  const [endTime, setEndTime] = useState(() => {
    const now = new Date();
    const later = new Date(now.getTime() + 60 * 60 * 1000);
    return `${String(later.getHours()).padStart(2, '0')}:${String(later.getMinutes()).padStart(2, '0')}`;
  });
  const [activePreset, setActivePreset] = useState('week-all');
  // Default to all-day — the correct mental model for digital signage
  // (screen is already on 24/7; no reason to gate playback by time of day
  // unless the user explicitly opts into a daily window).
  const [allDay, setAllDay] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  // Cache of existing slots per screen for conflict detection
  const [existingSlotsByScreen, setExistingSlotsByScreen] = useState({});
  // Schedule templates (time presets) state
  const [templates, setTemplates] = useState([]);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');

  // Legacy assets were stored without dimensions in the DB. Rather than
  // block scheduling or force a re-upload, probe the actual image/video
  // from its CDN URL when the modal opens. Browsers give us
  // naturalWidth/naturalHeight (images) and videoWidth/videoHeight
  // (videos) for free once the resource loads.
  const [probedDims, setProbedDims] = useState(null);
  useEffect(() => {
    if (!open) { setProbedDims(null); return undefined; }
    if (item?.width && item?.height) { setProbedDims(null); return undefined; }
    const url = item?.url || item?.thumbnailUrl || item?.cdn_url || item?.data?.url;
    if (!url) return undefined;
    let cancelled = false;
    // No crossOrigin: naturalWidth/naturalHeight and videoWidth/videoHeight
    // are readable even on tainted (non-CORS) loads — we're not reading
    // pixels. Setting it to 'anonymous' actively breaks the probe when the
    // CDN isn't CORS-configured, which is exactly this bucket's default.
    const isVideo = (item?.type || item?.mediaType || '').toString().startsWith('video');
    if (isVideo) {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.onloadedmetadata = () => {
        if (cancelled) return;
        const w = v.videoWidth || 0;
        const h = v.videoHeight || 0;
        if (w > 0 && h > 0) setProbedDims({ width: w, height: h });
      };
      v.src = url;
      return () => { cancelled = true; v.src = ''; };
    }
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      const w = img.naturalWidth || 0;
      const h = img.naturalHeight || 0;
      if (w > 0 && h > 0) setProbedDims({ width: w, height: h });
    };
    img.onerror = () => {
      // Silent fallback — banner stays in "unknown" state, user can still submit.
    };
    img.src = url;
    return () => { cancelled = true; };
  }, [open, item?.url, item?.thumbnailUrl, item?.cdn_url, item?.width, item?.height, item?.type, item?.mediaType]);

  const effectiveWidth = item?.width || probedDims?.width || null;
  const effectiveHeight = item?.height || probedDims?.height || null;

  // Reset state when the modal opens for a new item
  useEffect(() => {
    if (!open) return;
    const today = todayISO();
    const week = addDaysISO(today, 6);
    setSelectedScreenIds([]);
    setScreenSearch('');
    setStartDate(today);
    setEndDate(week);
    setStartTime('00:00');
    setEndTime('23:59');
    setAllDay(true);
    setActivePreset('week-all');
    setSaving(false);
    setSubmitMessage(null);
    setSubmitError(null);
    setExistingSlotsByScreen({});
    setTemplates(listTemplatesSorted());
    setShowSaveTemplate(false);
    setTemplateName('');
  }, [open, item?.id, item?.contentId]);

  const presets = useMemo(() => buildPresets(), []);

  const applyPreset = (preset) => {
    const v = preset.apply();
    setStartDate(v.startDate);
    setEndDate(v.endDate);
    setStartTime(v.startTime);
    setEndTime(v.endTime);
    if (typeof v.allDay === 'boolean') setAllDay(v.allDay);
    setActivePreset(preset.id);
  };

  const applyTemplate = (tpl) => {
    const { startDate: sd, endDate: ed } = datesForTemplate(tpl);
    setStartDate(sd);
    setEndDate(ed);
    setStartTime(tpl.startTime);
    setEndTime(tpl.endTime);
    // Templates always carry a specific time window — opting out of all-day.
    setAllDay(false);
    setActivePreset('custom');
  };

  const handleSaveTemplate = () => {
    try {
      const durationDays = Math.max(1, Math.round(
        (new Date(endDate).getTime() - new Date(startDate).getTime()) / 86400000
      ) + 1);
      saveTemplate({
        name: templateName,
        startTime,
        endTime,
        durationDays,
      });
      setTemplates(listTemplatesSorted());
      setShowSaveTemplate(false);
      setTemplateName('');
    } catch (err) {
      setSubmitError(err?.message || 'Could not save template');
    }
  };

  const handleDeleteTemplate = (id) => {
    deleteTemplate(id);
    setTemplates(listTemplatesSorted());
  };

  // Drop the active preset when the user manually edits any time/date field
  const markCustom = useCallback(() => {
    setActivePreset('custom');
  }, []);

  // Compatibility classification per screen. Uses effectiveWidth/Height
  // so client-probed dims (for assets without stored w/h) work too.
  const screenCompat = useMemo(() => {
    const map = {};
    tvs.forEach((tv) => {
      const id = tv.deviceId || tv.tvId;
      if (!id) return;
      map[id] = classifyScreen(tv, effectiveWidth, effectiveHeight);
    });
    return map;
  }, [tvs, effectiveWidth, effectiveHeight]);

  // Filter + group screens for the picker. Matches Screens-tab semantics:
  // registered devices only (profileId + displayName), grouped by human
  // location when set, else falling back to the site category derived from
  // the device ID. Incompatible screens are hidden — they can't be selected
  // anyway and only add visual noise to the picker.
  const groupedScreens = useMemo(() => {
    const q = screenSearch.toLowerCase();
    const grouped = {};
    tvs.forEach((tv) => {
      const id = tv.deviceId || tv.tvId || '';
      if (!tv.profileId || !tv.displayName) return;
      if (screenCompat[id]?.status === 'incompatible') return;
      const name = getDeviceDisplayName(tv).toLowerCase();
      if (q && !name.includes(q) && !id.toLowerCase().includes(q)) return;
      const loc = (tv.location || '').trim();
      const cat = loc || formatCategoryLabel(getScreenCategoryFromDeviceId(id));
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(tv);
    });
    return grouped;
  }, [tvs, screenSearch, screenCompat]);

  // Fetch existing slots for conflict detection whenever the screen
  // selection or date range changes. Debounced via single effect.
  useEffect(() => {
    if (!open || selectedScreenIds.length === 0) {
      setExistingSlotsByScreen({});
      return;
    }
    let cancelled = false;
    const fetchAll = async () => {
      const next = {};
      for (const sid of selectedScreenIds) {
        try {
          const res = await getCmsSchedules({ projectId: getActiveProjectId(), screenId: sid });
          if (cancelled) return;
          const list = res?.data || [];
          next[sid] = list;
        } catch {
          next[sid] = [];
        }
      }
      if (!cancelled) setExistingSlotsByScreen(next);
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [open, selectedScreenIds, startDate, endDate]);

  // Compute conflict info for the preview section
  const conflictSummary = useMemo(() => {
    if (selectedScreenIds.length === 0) return null;
    const sM = timeToMinutes(startTime);
    const eM = timeToMinutes(endTime);
    if (sM < 0 || eM < 0 || sM >= eM) return null;
    const schedulingContentId = item?.contentId || item?.id;
    const conflicts = [];
    for (const sid of selectedScreenIds) {
      const slots = existingSlotsByScreen[sid] || [];
      for (const slot of slots) {
        if ((slot.status || '').toLowerCase() === 'cancelled') continue;
        const slotStart = timeToMinutes(slot.startTime);
        const slotEnd = timeToMinutes(slot.endTime);
        if (slotStart < 0 || slotEnd < 0) continue;
        if (slotsOverlap(sM, eM, slotStart, slotEnd)) {
          const screen = tvs.find((t) => (t.deviceId || t.tvId) === sid);
          // Check if this content is already in the conflicting slot (primary or loop item)
          const loopIds = (slot.loopItems || []).map((li) => li.contentId);
          const alreadyScheduled = schedulingContentId && (
            slot.contentId === schedulingContentId || loopIds.includes(schedulingContentId)
          );
          conflicts.push({
            screenName: getDeviceDisplayName(screen),
            contentId: slot.contentId,
            startTime: slot.startTime,
            endTime: slot.endTime,
            alreadyScheduled,
          });
        }
      }
    }
    return conflicts;
  }, [selectedScreenIds, existingSlotsByScreen, startTime, endTime, tvs, item]);

  // Counts for the compat banner. unknown_content no longer blocks — we
  // surface it in `unknown` so the banner nudges without hard-blocking.
  const compatCounts = useMemo(() => {
    let compat = 0, incompat = 0, unknown = 0;
    tvs.forEach((tv) => {
      if (!tv.profileId || !tv.displayName) return;
      const id = tv.deviceId || tv.tvId;
      const c = screenCompat[id]?.status;
      if (c === 'compatible') compat += 1;
      else if (c === 'incompatible') incompat += 1;
      else unknown += 1;
    });
    return { compat, incompat, unknown };
  }, [tvs, screenCompat]);

  const contentDimsKnown = !!(effectiveWidth && effectiveHeight);

  // A screen is blocked ONLY when its AR conflicts with known content dims.
  // If content dims are unknown we warn (banner + tooltip) but let the user
  // schedule — backend runs its own AR check and will reject mismatches.
  // The old behaviour of gating every screen on missing dims made legacy
  // assets (uploaded before dims-capture was wired through everywhere)
  // unschedulable with no recovery path except re-uploading.
  const isScreenBlocked = (id) => {
    const s = screenCompat[id]?.status;
    return s === 'incompatible';
  };

  const toggleScreen = (id) => {
    if (isScreenBlocked(id)) return;
    setSelectedScreenIds((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]);
  };

  const duration = formatDuration(startTime, endTime);

  // Validation for the submit button. Overnight (end < start) is allowed:
  // the schedule means "play from <start> tonight through <end> next morning".
  // It submits as two back-to-back schedules under the hood so each fits
  // into the single-day slot model the backend evaluates against.
  const validationError = useMemo(() => {
    if (selectedScreenIds.length === 0) return 'Pick at least one screen.';
    if (!startDate) return 'Pick a start date.';
    if (!endDate) return 'Pick an end date.';
    if (!allDay) {
      const sM = timeToMinutes(startTime);
      const eM = timeToMinutes(endTime);
      if (sM < 0) return 'Start time is not valid.';
      if (eM < 0) return 'End time is not valid.';
      if (sM === eM) return 'Start and end time are the same — pick a duration.';
    }
    return null;
  }, [selectedScreenIds, startDate, endDate, startTime, endTime, allDay]);
  const isValid = validationError === null;
  const isOvernight = useMemo(() => {
    if (allDay) return false;
    const sM = timeToMinutes(startTime);
    const eM = timeToMinutes(endTime);
    return sM >= 0 && eM >= 0 && eM < sM;
  }, [startTime, endTime, allDay]);

  // Live preview sentence
  const previewSentence = useMemo(() => {
    if (selectedScreenIds.length === 0) return 'Pick at least one screen to see the preview.';
    if (!isValid) return 'Set a valid date and time range to see the preview.';
    const screenNames = selectedScreenIds
      .map((sid) => getDeviceDisplayName(tvs.find((t) => (t.deviceId || t.tvId) === sid)))
      .filter(Boolean);
    const firstName = screenNames[0];
    const screenPart = screenNames.length === 1
      ? firstName
      : `${firstName} and ${screenNames.length - 1} more screen${screenNames.length > 2 ? 's' : ''}`;
    const recurrenceText = startDate === endDate ? 'one time' : 'every day';
    const dateRange = startDate === endDate
      ? formatDateRange(startDate)
      : formatDateRange(startDate, endDate);
    const overnightNote = isOvernight
      ? ' (overnight — saved as two linked schedules: evening + next-morning).'
      : '';
    const airtimeText = allDay
      ? 'all day (24h)'
      : `${formatTimeReadable(startTime)} to ${formatTimeReadable(endTime)} (${duration})`;
    return `"${item?.title || item?.id}" will play on ${screenPart}, ${dateRange}, ${airtimeText}, ${recurrenceText}.${overnightNote}`;
  }, [selectedScreenIds, isValid, tvs, startDate, endDate, startTime, endTime, item, duration, isOvernight, allDay]);

  // Submit
  const handleSubmit = async () => {
    if (!isValid) return;
    setSaving(true);
    setSubmitMessage(null);
    setSubmitError(null);
    try {
      // Defensive re-filter: refuse wrong-AR screens only. When content dims
      // are unknown we allow the submit; server-side AR check is the final
      // guard and will 400 if the aspect ratio actually mismatches.
      const validIds = selectedScreenIds.filter((sid) => {
        const screen = tvs.find((t) => (t.deviceId || t.tvId) === sid);
        const s = classifyScreen(screen, effectiveWidth, effectiveHeight).status;
        return s !== 'incompatible';
      });
      if (validIds.length === 0) {
        setSubmitError('All selected screens have incompatible aspect ratios.');
        setSaving(false);
        return;
      }
      let ok = 0;
      let fail = 0;
      const skipped = selectedScreenIds.length - validIds.length;
      // Date-range recurrence: intent plays every day in [startDate, endDate]
      // during [startTime, endTime]. No daysOfWeek filtering — a week is a week.
      const shiftISO = (iso, delta) => {
        const d = new Date(`${iso}T00:00:00`);
        d.setDate(d.getDate() + delta);
        return d.toISOString().slice(0, 10);
      };

      const buildPayloads = (screenId) => {
        const baseContentId = item?.contentId || item?.content_id || item?.id;
        const baseName = item?.title || item?.name || baseContentId;
        const mkPayload = (sTime, eTime, dateOverrides) => ({
          projectId: getActiveProjectId(),
          screenId,
          contentId: baseContentId,
          name: baseName,
          startTime: sTime,
          endTime: eTime,
          ...dateOverrides,
        });
        const dates = startDate === endDate
          ? { date: startDate }
          : { startDate, endDate };

        // All-day short-circuits overnight splitting: 00:00–23:59 never needs
        // a two-intent workaround and any stale startTime/endTime state from
        // a previously clicked preset is overridden.
        if (allDay) {
          return [mkPayload('00:00', '23:59', dates)];
        }

        if (!isOvernight) {
          return [mkPayload(startTime, endTime, dates)];
        }

        // Overnight: split into two back-to-back schedules.
        //   Evening: [startTime, 23:59] on the scheduled days.
        //   Morning: [00:00, endTime] on the next calendar day.
        const eveningPayload = mkPayload(startTime, '23:59', dates);
        const morningDates = startDate === endDate
          ? { date: shiftISO(startDate, 1) }
          : { startDate: shiftISO(startDate, 1), endDate: shiftISO(endDate, 1) };
        const morningPayload = mkPayload('00:00', endTime, morningDates);
        return [eveningPayload, morningPayload];
      };

      for (const screenId of validIds) {
        const payloads = buildPayloads(screenId);
        let screenOk = true;
        for (const payload of payloads) {
          try {
            await createScheduleFromLegacyShape(payload);
          } catch (err) {
            screenOk = false;
            console.warn('Schedule create failed for', screenId, err?.message);
          }
        }
        if (screenOk) ok += 1;
        else fail += 1;
      }
      const skipNote = skipped > 0 ? ` (${skipped} skipped — wrong AR)` : '';
      if (fail === 0) {
        setSubmitMessage(`Scheduled on ${ok} screen${ok === 1 ? '' : 's'}${skipNote}.`);
        if (typeof onScheduled === 'function') onScheduled({ ok, fail, skipped });
        setTimeout(() => onClose(), 1100);
      } else {
        setSubmitError(`${ok} scheduled, ${fail} failed${skipNote}.`);
      }
    } catch (err) {
      setSubmitError(err?.message || 'Failed to schedule.');
    } finally {
      setSaving(false);
    }
  };

  if (!open || !item) return null;

  const buttonLabel = saving
    ? 'Scheduling…'
    : selectedScreenIds.length === 0
      ? 'Schedule'
      : `Schedule on ${selectedScreenIds.length} screen${selectedScreenIds.length === 1 ? '' : 's'}`;

  return (
    <div style={styles.overlay} onClick={() => !saving && onClose()}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>Schedule content</h2>
          <button type="button" style={styles.closeBtn} onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {/* Content header — thumbnail + metadata */}
        <div style={styles.contentBlock}>
          {item.url ? (
            <img
              src={item.url}
              alt={item.title || item.id}
              style={styles.thumb}
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
          ) : (
            <div style={{ ...styles.thumb, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
              <Monitor size={24} />
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={styles.contentName}>{item.title || item.id}</div>
            <div style={styles.contentMeta}>
              {item.type === 'video' ? 'Video' : 'Image'}
              {item.width && item.height && ` · ${item.width}×${item.height}`}
              {item.width && item.height && ` · ${(() => {
                const r = checkAspectRatioMatch(item.width, item.height, item.width, item.height);
                return r.actual;
              })()}`}
              {item.width && item.height && (item.height > item.width ? ' portrait' : ' landscape')}
            </div>
          </div>
        </div>

        <div style={styles.body}>
          {/* WHERE */}
          <div style={styles.section}>
            <div style={styles.sectionLabel}>
              <Monitor size={12} /> Where
            </div>
            <input
              type="text"
              placeholder="Search screens by name or ID…"
              value={screenSearch}
              onChange={(e) => setScreenSearch(e.target.value)}
              style={{ ...styles.input, marginBottom: 8 }}
            />
            {/* Compatibility banner */}
            {!contentDimsKnown ? (
              <div style={{
                padding: '6px 10px',
                background: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: 6,
                fontSize: 11,
                color: '#92400e',
                marginBottom: 8,
              }}>
                ⚠ Content resolution unknown — aspect-ratio compatibility can't be verified. Scheduling is allowed; the server will reject a mismatch if one exists.
              </div>
            ) : (compatCounts.incompat > 0 || compatCounts.unknown > 0) && (
              <div style={{
                padding: '6px 10px',
                background: compatCounts.incompat > 0 ? '#fffbeb' : '#f0f9ff',
                border: `1px solid ${compatCounts.incompat > 0 ? '#fde68a' : '#bae6fd'}`,
                borderRadius: 6,
                fontSize: 11,
                color: compatCounts.incompat > 0 ? '#92400e' : '#075985',
                marginBottom: 8,
              }}>
                <strong>{compatCounts.compat}</strong> compatible
                {compatCounts.incompat > 0 && <> · <strong>{compatCounts.incompat}</strong> wrong AR (hidden)</>}
                {compatCounts.unknown > 0 && <> · <strong>{compatCounts.unknown}</strong> screen resolution not yet reported</>}
              </div>
            )}
            <div style={styles.screenList}>
              {Object.keys(groupedScreens).length === 0 ? (
                <div style={{ padding: 14, fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>
                  No screens match your search.
                </div>
              ) : (
                Object.keys(groupedScreens)
                  .sort((a, b) => {
                    if (a === 'Uncategorized') return 1;
                    if (b === 'Uncategorized') return -1;
                    return a.localeCompare(b);
                  })
                  .map((cat) => {
                    const screens = groupedScreens[cat];
                    const selectableIds = screens
                      .filter((tv) => !isScreenBlocked(tv.deviceId || tv.tvId))
                      .map((tv) => tv.deviceId || tv.tvId);
                    const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedScreenIds.includes(id));
                    return (
                      <div key={cat}>
                        <div style={styles.catHeader}>
                          <span>{cat}</span>
                          {selectableIds.length > 0 && (
                            <button
                              type="button"
                              style={{ border: 'none', background: 'transparent', color: '#2563eb', cursor: 'pointer', fontSize: 10, fontWeight: 600 }}
                              onClick={() => {
                                setSelectedScreenIds((prev) =>
                                  allSelected
                                    ? prev.filter((id) => !selectableIds.includes(id))
                                    : [...new Set([...prev, ...selectableIds])]
                                );
                              }}
                            >
                              {allSelected ? 'Deselect all' : 'Select all'}
                            </button>
                          )}
                        </div>
                        {screens.map((tv) => {
                          const id = tv.deviceId || tv.tvId;
                          const checked = selectedScreenIds.includes(id);
                          const compat = screenCompat[id] || { status: 'unknown_screen' };
                          const blocked = isScreenBlocked(id);
                          const tooltip = compat.status === 'incompatible'
                            ? `Wrong aspect ratio — content is ${compat.actualOrientation} (${compat.actual}), screen is ${compat.expectedOrientation} (${compat.expected})`
                            : compat.status === 'unknown_content'
                              ? 'Content resolution unavailable — cannot verify compatibility'
                              : compat.status === 'unknown_screen'
                                ? 'Screen resolution not yet reported — scheduling allowed'
                                : 'Aspect ratio matches your content';
                          return (
                            <label
                              key={id}
                              title={tooltip}
                              style={styles.screenRow(checked, blocked)}
                              onClick={(e) => {
                                if (blocked) e.preventDefault();
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={checked && !blocked}
                                disabled={blocked}
                                onChange={() => toggleScreen(id)}
                                style={{ accentColor: '#c8a147' }}
                              />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, color: '#0f172a' }}>{getDeviceDisplayName(tv)}</div>
                                <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>
                                  {getResolutionLabel(tv) || 'unknown size'}
                                  {compat.status === 'compatible' && (
                                    <span style={{ color: '#15803d', marginLeft: 6 }}>
                                      ✓ matches your AR
                                    </span>
                                  )}
                                  {compat.status === 'incompatible' && (
                                    <span style={{ color: '#dc2626', marginLeft: 6, fontWeight: 600 }}>
                                      ⊘ wrong AR
                                    </span>
                                  )}
                                </div>
                              </div>
                              <span style={{ fontSize: 10, color: tv.status === 'online' ? '#10b981' : '#94a3b8' }}>
                                {tv.status}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })
              )}
            </div>
          </div>

          {/* Split-screen is disabled — intent schema doesn't support zones yet. */}

          {/* WHEN */}
          <div style={styles.section}>
            <div style={styles.sectionLabel}>
              <Clock size={12} /> When
            </div>

            {/* Saved templates row — only renders if user has any */}
            {templates.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Bookmark size={11} /> Your saved patterns
                </div>
                <div style={styles.presetRow}>
                  {templates.map((tpl) => (
                    <div
                      key={tpl.id}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        padding: '4px 4px 4px 10px',
                        fontSize: 11,
                        fontWeight: 600,
                        borderRadius: 16,
                        border: '1px solid #ddd6fe',
                        background: '#ede9fe',
                        color: '#5b21b6',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => applyTemplate(tpl)}
                        title={`Apply: ${tpl.startTime}–${tpl.endTime}`}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: 'inherit',
                          cursor: 'pointer',
                          fontWeight: 600,
                          fontSize: 11,
                          padding: 0,
                        }}
                      >
                        {tpl.name}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteTemplate(tpl.id)}
                        title="Delete template"
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: '#7c3aed',
                          cursor: 'pointer',
                          padding: '0 4px',
                          display: 'flex',
                          alignItems: 'center',
                          opacity: 0.6,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.opacity = 1; }}
                        onMouseLeave={(e) => { e.currentTarget.style.opacity = 0.6; }}
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick presets — last chip is an explicit "Custom" toggle
                that just drops into edit-your-own mode without mutating
                the date/time fields, so users can clearly see "I'm not
                on a preset" and edit freely. */}
            <div style={styles.presetRow}>
              {presets.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => applyPreset(p)}
                  style={styles.presetChip(activePreset === p.id)}
                >
                  {p.label}
                </button>
              ))}
              <button
                type="button"
                onClick={markCustom}
                style={styles.presetChip(activePreset === 'custom')}
              >
                Custom
              </button>
            </div>

            {/* Two-section layout separating the date range ("when is this
                campaign live") from the time-of-day window ("what hours
                does it play"). Defaults to all-day; users opt into a time
                window when they actually need one (café morning menu etc). */}
            {(() => {
              const locked = activePreset !== 'custom';
              const lockedStyle = locked
                ? { ...styles.input, backgroundColor: '#f8fafc', color: '#475569', cursor: 'not-allowed' }
                : styles.input;
              return (
                <>
                  <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 4, marginBottom: 6 }}>
                    Campaign run
                  </div>
                  <div style={styles.twoCol}>
                    <div style={styles.field}>
                      <label style={styles.label}>From</label>
                      <input
                        type="date"
                        value={startDate}
                        disabled={locked}
                        readOnly={locked}
                        onChange={(e) => {
                          const v = e.target.value;
                          setStartDate(v);
                          if (!endDate || endDate < v) setEndDate(v);
                          markCustom();
                        }}
                        style={lockedStyle}
                      />
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>Until</label>
                      <input
                        type="date"
                        value={endDate}
                        min={startDate}
                        disabled={locked}
                        readOnly={locked}
                        onChange={(e) => { setEndDate(e.target.value); markCustom(); }}
                        style={lockedStyle}
                      />
                    </div>
                  </div>

                  <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 12, marginBottom: 6 }}>
                    Daily airtime
                  </div>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 10px',
                      border: '1px solid #e5e7eb',
                      borderRadius: 6,
                      fontSize: 13,
                      cursor: locked ? 'not-allowed' : 'pointer',
                      backgroundColor: locked ? '#f8fafc' : '#fff',
                      color: locked ? '#475569' : '#111827',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={allDay}
                      disabled={locked}
                      onChange={(e) => { setAllDay(e.target.checked); markCustom(); }}
                      style={{ cursor: locked ? 'not-allowed' : 'pointer' }}
                    />
                    <span>
                      <strong>Plays all day (24 hours)</strong>
                      <span style={{ marginLeft: 6, color: '#94a3b8', fontWeight: 400 }}>
                        — uncheck to set specific hours
                      </span>
                    </span>
                  </label>

                  {!allDay && (
                    <div style={{ ...styles.twoCol, marginTop: 8 }}>
                      <div style={styles.field}>
                        <label style={styles.label}>Start time</label>
                        <input
                          type="time"
                          value={startTime}
                          disabled={locked}
                          readOnly={locked}
                          onChange={(e) => { setStartTime(e.target.value); markCustom(); }}
                          style={lockedStyle}
                        />
                      </div>
                      <div style={styles.field}>
                        <label style={styles.label}>
                          End time
                          {duration && (
                            <span style={{ marginLeft: 8, color: '#94a3b8', fontWeight: 400 }}>
                              ({duration})
                            </span>
                          )}
                        </label>
                        <input
                          type="time"
                          value={endTime}
                          disabled={locked}
                          readOnly={locked}
                          onChange={(e) => { setEndTime(e.target.value); markCustom(); }}
                          style={lockedStyle}
                        />
                      </div>
                    </div>
                  )}
                </>
              );
            })()}

            {/* Day timeline visualization — one stacked row per selected screen.
                Drag-to-edit works on every row; they all share the same proposed
                slot (start/end), so dragging any row updates all of them in sync. */}
            {selectedScreenIds.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 6 }}>
                  Drag the gold bar to move it · drag the edges to resize
                  {selectedScreenIds.length > 1 && (
                    <> · changes apply to all {selectedScreenIds.length} screens</>
                  )}
                </div>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  maxHeight: selectedScreenIds.length > 3 ? 320 : undefined,
                  overflowY: selectedScreenIds.length > 3 ? 'auto' : undefined,
                }}>
                  {selectedScreenIds.map((sid) => {
                    const screen = tvs.find((t) => (t.deviceId || t.tvId) === sid);
                    const screenName = screen ? getDeviceDisplayName(screen) : sid;
                    const slotsForScreen = existingSlotsByScreen[sid];
                    // Per-row conflict count for the row label
                    const rowConflictCount = (() => {
                      if (!Array.isArray(slotsForScreen)) return null;
                      const sM = timeToMinutes(startTime);
                      const eM = timeToMinutes(endTime);
                      if (sM < 0 || eM <= sM) return 0;
                      let n = 0;
                      slotsForScreen.forEach((slot) => {
                        if ((slot.status || '').toLowerCase() === 'cancelled') return;
                        const ss = timeToMinutes(slot.startTime);
                        const se = timeToMinutes(slot.endTime);
                        if (ss < 0 || se <= ss) return;
                        if (slotsOverlap(sM, eM, ss, se)) n += 1;
                      });
                      return n;
                    })();
                    return (
                      <div key={sid}>
                        {/* Row label — only when multi-screen */}
                        {selectedScreenIds.length > 1 && (
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '0 4px 3px',
                            fontSize: 11,
                          }}>
                            <span style={{ fontWeight: 600, color: '#475569' }}>{screenName}</span>
                            {rowConflictCount === 0 && (
                              <span style={{ color: '#15803d', fontSize: 10, fontWeight: 500 }}>✓ no conflicts</span>
                            )}
                            {rowConflictCount > 0 && (
                              <span style={{ color: '#b45309', fontSize: 10, fontWeight: 600 }}>
                                ⚠ {rowConflictCount} conflict{rowConflictCount === 1 ? '' : 's'} — will auto-merge
                              </span>
                            )}
                            {rowConflictCount === null && (
                              <span style={{ color: '#cbd5e1', fontSize: 10, fontStyle: 'italic' }}>loading…</span>
                            )}
                          </div>
                        )}
                        <ScheduleTimeline
                          existingSlots={slotsForScreen}
                          proposedStart={startTime}
                          proposedEnd={endTime}
                          proposedTitle={item?.title || item?.id || 'New slot'}
                          onProposedChange={(newStart, newEnd) => {
                            setStartTime(newStart);
                            setEndTime(newEnd);
                            markCustom();
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Save as template */}
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e2e8f0' }}>
              {!showSaveTemplate ? (
                <button
                  type="button"
                  onClick={() => setShowSaveTemplate(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '5px 10px',
                    fontSize: 11,
                    fontWeight: 600,
                    border: '1px dashed #cbd5e1',
                    background: '#fff',
                    color: '#64748b',
                    borderRadius: 6,
                    cursor: 'pointer',
                  }}
                >
                  <Bookmark size={11} /> Save this time pattern as a template
                </button>
              ) : (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="e.g. Weekday lunch"
                    maxLength={60}
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSaveTemplate();
                      if (e.key === 'Escape') { setShowSaveTemplate(false); setTemplateName(''); }
                    }}
                    style={{ ...styles.input, flex: 1, fontSize: 12 }}
                  />
                  <button
                    type="button"
                    onClick={handleSaveTemplate}
                    disabled={!templateName.trim()}
                    style={{
                      padding: '7px 12px',
                      fontSize: 11,
                      fontWeight: 600,
                      border: '1px solid #c8a147',
                      background: templateName.trim() ? 'rgba(200, 161, 71, 0.15)' : '#f1f5f9',
                      color: templateName.trim() ? '#92400e' : '#94a3b8',
                      borderRadius: 6,
                      cursor: templateName.trim() ? 'pointer' : 'not-allowed',
                    }}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowSaveTemplate(false); setTemplateName(''); }}
                    style={{
                      padding: '7px 10px',
                      fontSize: 11,
                      border: '1px solid #e5e7eb',
                      background: '#fff',
                      color: '#6b7280',
                      borderRadius: 6,
                      cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                </div>
              )}
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
                Templates save the time pattern (e.g. "weekday lunch") so you can reuse it on other content. They don't save the screens or content itself.
              </div>
            </div>
          </div>

          {/* PREVIEW */}
          <div style={styles.section}>
            <div style={styles.sectionLabel}>
              <Calendar size={12} /> Preview
            </div>
            <div style={styles.preview}>
              {previewSentence}
              {selectedScreenIds.length > 0 && isValid && (
                conflictSummary && conflictSummary.length > 0 ? (() => {
                  const allAlready = conflictSummary.every((c) => c.alreadyScheduled);
                  const newConflicts = conflictSummary.filter((c) => !c.alreadyScheduled);
                  return (
                    <div style={allAlready ? styles.conflictNone : styles.conflictHas}>
                      {allAlready ? (
                        <><Check size={13} /><span>Already in the loop on selected screens — no change.</span></>
                      ) : (
                        <>
                          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                          <div>
                            <strong>{newConflicts.length} conflict{newConflicts.length === 1 ? '' : 's'}</strong> with existing schedules — they will auto-merge into a loop:
                            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                              {newConflicts.slice(0, 3).map((c, i) => (
                                <li key={i}>
                                  {c.screenName}: <code>{c.contentId}</code> ({c.startTime}–{c.endTime})
                                </li>
                              ))}
                              {newConflicts.length > 3 && (
                                <li>… and {newConflicts.length - 3} more</li>
                              )}
                            </ul>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })() : (
                  <div style={styles.conflictNone}>
                    <Check size={14} /> No conflicts on the selected screen(s) during these hours.
                  </div>
                )
              )}
            </div>
            {submitError && <div style={styles.errorBox}>{submitError}</div>}
            {submitMessage && (
              <div style={{ ...styles.errorBox, background: '#f0fdf4', borderColor: '#bbf7d0', color: '#15803d' }}>
                {submitMessage}
              </div>
            )}
          </div>
        </div>

        <div style={styles.footer}>
          {validationError && (
            <div style={{
              flex: 1,
              minWidth: 0,
              fontSize: 12,
              color: '#b91c1c',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 6,
              padding: '6px 10px',
              marginRight: 8,
              lineHeight: 1.35,
            }}>
              {validationError}
            </div>
          )}
          <button type="button" style={styles.cancelBtn} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            style={styles.primaryBtn(!isValid || saving)}
            onClick={handleSubmit}
            disabled={!isValid || saving}
            title={validationError || undefined}
          >
            {buttonLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleEventModal;
